/**
 * Utilidades de formateo para el módulo Registros de Uso
 * Funciones para formatear datos de manera consistente
 */

/**
 * Formatea un número de teléfono
 * @param {string|number} phone - Número de teléfono
 * @param {boolean} abbreviated - Si debe mostrar versión abreviada
 * @returns {string} Teléfono formateado
 * 
 * @example
 * formatPhoneNumber('3115678901') // '311 567 8901'
 * formatPhoneNumber('3115678901', true) // '311...'
 */
export const formatPhoneNumber = (phone, abbreviated = false) => {
    if (!phone) return '';
    
    const phoneStr = phone.toString();
    
    if (abbreviated) {
        return `${phoneStr.substring(0, 3)}...`;
    }
    
    // Formato: 311 567 8901
    if (phoneStr.length === 10) {
        return `${phoneStr.substring(0, 3)} ${phoneStr.substring(3, 6)} ${phoneStr.substring(6)}`;
    }
    
    return phoneStr;
};

/**
 * Formatea un porcentaje
 * @param {number} value - Valor numérico
 * @param {number} decimals - Cantidad de decimales (default: 0)
 * @returns {string} Porcentaje formateado
 * 
 * @example
 * formatPercentage(75.5) // '76%'
 * formatPercentage(75.5, 1) // '75.5%'
 */
export const formatPercentage = (value, decimals = 0) => {
    if (value === null || value === undefined) return '0%';
    return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Formatea un número con separadores de miles
 * @param {number} value - Valor numérico
 * @returns {string} Número formateado
 * 
 * @example
 * formatNumber(1234567) // '1,234,567'
 */
export const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return Number(value).toLocaleString('es-CO');
};

/**
 * Formatea una fecha
 * @param {Date|string} date - Fecha a formatear
 * @param {string} format - Formato deseado ('short', 'long', 'full')
 * @returns {string} Fecha formateada
 * 
 * @example
 * formatDate(new Date(), 'short') // '03/02/2026'
 * formatDate(new Date(), 'long') // '3 de Febrero de 2026'
 */
export const formatDate = (date, format = 'short') => {
    if (!date) return '';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) return '';
    
    switch (format) {
        case 'short':
            return dateObj.toLocaleDateString('es-CO');
        case 'long':
            return dateObj.toLocaleDateString('es-CO', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        case 'full':
            return dateObj.toLocaleDateString('es-CO', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        default:
            return dateObj.toLocaleDateString('es-CO');
    }
};

/**
 * Formatea un período (mes/año)
 * @param {number} month - Número del mes (1-12)
 * @param {number} year - Año
 * @param {Array} monthNames - Array de nombres de meses
 * @returns {string} Período formateado
 * 
 * @example
 * formatPeriod(6, 2025, MONTH_NAMES) // 'Junio 2025'
 */
export const formatPeriod = (month, year, monthNames) => {
    if (!month || !year || !monthNames) return 'Sin período';
    return `${monthNames[month - 1]} ${year}`;
};

/**
 * Formatea un documento de identificación
 * @param {string|number} id - Documento de identificación
 * @returns {string} Documento formateado
 * 
 * @example
 * formatIdentification('1075689548') // '1.075.689.548'
 */
export const formatIdentification = (id) => {
    if (!id) return '';
    
    const idStr = id.toString();
    return idStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/**
 * Trunca un texto a una longitud específica
 * @param {string} text - Texto a truncar
 * @param {number} maxLength - Longitud máxima
 * @param {string} suffix - Sufijo a agregar (default: '...')
 * @returns {string} Texto truncado
 * 
 * @example
 * truncateText('Texto muy largo', 10) // 'Texto muy...'
 */
export const truncateText = (text, maxLength, suffix = '...') => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + suffix;
};

/**
 * Formatea bytes a tamaño legible
 * @param {number} bytes - Cantidad de bytes
 * @param {number} decimals - Decimales a mostrar
 * @returns {string} Tamaño formateado
 * 
 * @example
 * formatBytes(1024) // '1 KB'
 * formatBytes(1048576) // '1 MB'
 */
export const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Convierte mes y año a rango de fechas formato YYYY-MM-DD
 * Calcula correctamente el último día del mes (incluye años bisiestos)
 * @param {number} month - Número del mes (1-12)
 * @param {number} year - Año (ej: 2026)
 * @returns {{ datestart: string, dateend: string }}
 * 
 * @example
 * getDateRange(2, 2026) // { datestart: '2026-02-01', dateend: '2026-02-28' }
 * getDateRange(2, 2024) // { datestart: '2024-02-01', dateend: '2024-02-29' }
 * getDateRange(12, 2025) // { datestart: '2025-12-01', dateend: '2025-12-31' }
 */
export const getDateRange = (month, year) => {
    if (!month || !year) return { datestart: '', dateend: '' };

    const monthStr = String(month).padStart(2, '0');
    const lastDay = new Date(year, month, 0).getDate();

    return {
        datestart: `${year}-${monthStr}-01`,
        dateend: `${year}-${monthStr}-${String(lastDay).padStart(2, '0')}`,
    };
};

export default {
    formatPhoneNumber,
    formatPercentage,
    formatNumber,
    formatDate,
    formatPeriod,
    formatIdentification,
    truncateText,
    formatBytes,
    getDateRange,
};
